import React, { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { DocumentReferencesView } from '../api';
import FilesService from '../services/documents';
import { Logger } from '../utilities';

export interface DocumentReference {
  documentId: number;
  documentName?: string;
  fields: Array<{
    fieldName: string;
    sheetName: string;
    cellReferences: Array<{
      ref: string;
      direction: 'positive' | 'negative';
    }>;
  }>;
}

interface DocumentFootnotesContextType {
  footnotes: Map<string, DocumentReference[]>;
  documentSourceMap: Map<number, number>; // Maps document ID to source number
  registerFootnote: (footnoteId: string, documents: DocumentReference[]) => void;
  unregisterFootnote: (footnoteId: string) => void;
  clearFootnotes: () => void;
  getSourceNumber: (documentId: number) => number | undefined;
  getDocumentMetadata: (documentId: number) => DocumentReference | undefined;
  setDocumentNames: (documentNames: Map<number, string>) => void;
}

const DocumentFootnotesContext = createContext<DocumentFootnotesContextType | undefined>(undefined);

export const DocumentFootnotesProvider = ({ children }: { children: ReactNode }) => {
  const [footnotes, setFootnotes] = useState<Map<string, DocumentReference[]>>(new Map());
  const [documentNames, setDocumentNames] = useState<Map<number, string>>(new Map());
  const filesService = useRef(new FilesService()).current;
  const fetchingDocuments = useRef<Set<number>>(new Set()).current;

  // Create a map of document IDs to source numbers (1, 2, 3, etc.)
  const documentSourceMap = useMemo(() => {
    const uniqueDocIds = new Set<number>();
    footnotes.forEach(documents => {
      documents.forEach(doc => uniqueDocIds.add(doc.documentId));
    });

    const sourceMap = new Map<number, number>();
    Array.from(uniqueDocIds)
      .sort((a, b) => a - b)
      .forEach((docId, index) => {
        sourceMap.set(docId, index + 1);
      });

    return sourceMap;
  }, [footnotes]);

  // Create a consolidated metadata map from all footnotes
  const documentMetadataMap = useMemo(() => {
    const metadataMap = new Map<number, DocumentReference>();

    footnotes.forEach(documents => {
      documents.forEach(doc => {
        const existing = metadataMap.get(doc.documentId);
        if (existing) {
          // Merge fields, avoiding duplicates
          const existingFieldKeys = new Set(existing.fields.map(f => `${f.fieldName}-${f.sheetName}`));
          const newFields = doc.fields.filter(f => !existingFieldKeys.has(`${f.fieldName}-${f.sheetName}`));
          metadataMap.set(doc.documentId, {
            ...existing,
            fields: [...existing.fields, ...newFields],
          });
        } else {
          metadataMap.set(doc.documentId, doc);
        }
      });
    });

    // Merge in document names from the documentNames map
    documentNames.forEach((name, docId) => {
      const existing = metadataMap.get(docId);
      if (existing) {
        metadataMap.set(docId, {
          ...existing,
          documentName: name,
        });
      } else {
        // Create a new entry if this document ID doesn't exist yet
        metadataMap.set(docId, {
          documentId: docId,
          documentName: name,
          fields: [],
        });
      }
    });

    return metadataMap;
  }, [footnotes, documentNames]);

  const registerFootnote = useCallback((footnoteId: string, documents: DocumentReference[]) => {
    setFootnotes(prev => {
      const newMap = new Map(prev);
      newMap.set(footnoteId, documents);
      return newMap;
    });
  }, []);

  const unregisterFootnote = useCallback((footnoteId: string) => {
    setFootnotes(prev => {
      const newMap = new Map(prev);
      newMap.delete(footnoteId);
      return newMap;
    });
  }, []);

  const clearFootnotes = useCallback(() => {
    setFootnotes(new Map());
  }, []);

  const getSourceNumber = useCallback((documentId: number) => documentSourceMap.get(documentId), [documentSourceMap]);

  const fetchDocumentName = useCallback(
    async (documentId: number): Promise<void> => {
      // Avoid duplicate requests
      if (fetchingDocuments.has(documentId) || documentNames.has(documentId)) {
        return;
      }

      fetchingDocuments.add(documentId);

      try {
        const response = (await filesService.getDocumentById(String(documentId))) as DocumentReferencesView;
        if (response?.file?.filename) {
          setDocumentNames(prev => {
            const newMap = new Map(prev);
            newMap.set(documentId, response.file.filename);
            return newMap;
          });
        }
      } catch (error) {
        Logger.error(`Error fetching document name for ID ${documentId}`, error);
      } finally {
        fetchingDocuments.delete(documentId);
      }
    },
    [filesService, fetchingDocuments, documentNames]
  );

  const getDocumentMetadata = useCallback(
    (documentId: number) => {
      const metadata = documentMetadataMap.get(documentId);

      // If we have metadata but no document name, fetch it
      if (metadata && !metadata.documentName && !fetchingDocuments.has(documentId)) {
        fetchDocumentName(documentId);
      }

      return metadata;
    },
    [documentMetadataMap, fetchDocumentName, fetchingDocuments]
  );

  return (
    <DocumentFootnotesContext.Provider
      value={{
        footnotes,
        documentSourceMap,
        registerFootnote,
        unregisterFootnote,
        clearFootnotes,
        getSourceNumber,
        getDocumentMetadata,
        setDocumentNames,
      }}>
      {children}
    </DocumentFootnotesContext.Provider>
  );
};

export const useDocumentFootnotes = () => {
  const context = useContext(DocumentFootnotesContext);
  if (!context) {
    throw new Error('useDocumentFootnotes must be used within DocumentFootnotesProvider');
  }
  return context;
};
